/**
 * Message utilities with WebSocket/Realtime support
 */

import { subscribeWithAuth } from './supabaseClient';
import { parseAdShareMessage } from './adShareMessage';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const getStoredToken = () => {
  return localStorage.getItem('auth_token');
};

export const parseSystemMessage = (content) => {
  if (content == null) return null;
  const raw = typeof content === 'string' ? content : String(content);
  const match = raw.trim().match(/^SYSTEM_MESSAGE:\s*([\s\S]+)$/);
  if (!match) return null;
  try {
    const data = JSON.parse(match[1]);
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null;
  }
};

export const formatMessagePreview = (content) => {
  const system = parseSystemMessage(content);
  if (system) return system.title || system.message || 'Order update';
  const shared = parseAdShareMessage(content);
  if (shared) return shared.text || shared.share?.line1 || shared.share?.title || 'Listing inquiry';
  if (typeof content !== 'string') return '';
  return content.startsWith('SYSTEM_MESSAGE:') ? 'Order update' : content;
};

export const conversationDedupeKey = (conv) => {
  const user1 = conv.user_id;
  const user2 = conv.seller_id;
  const pair = user1 < user2 ? `${user1}_${user2}` : `${user2}_${user1}`;
  return `${pair}:${conv.request_id || 'dm'}:${conv.ad_id || 'none'}`;
};

// Normalize messages: dedupe by id and sort ascending by created_at (oldest -> newest)
const normalizeMessages = (messages) => {
  const arr = Array.isArray(messages) ? messages : [];
  const byId = new Map();
  for (const m of arr) {
    const id = m?.id;
    if (!id) continue;
    // Keep the latest version if duplicates exist
    byId.set(id, m);
  }
  const deduped = Array.from(byId.values());
  deduped.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  return deduped;
};

// Get cached messages from localStorage
export const getCachedMessages = (conversationId) => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const cacheKey = `messages_${conversationId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) {
        return normalizeMessages(parsed);
      }
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null;
};

// Cache messages to localStorage
export const cacheMessages = (conversationId, messages) => {
  if (typeof localStorage === 'undefined') return;
  try {
    const cacheKey = `messages_${conversationId}`;
    const normalized = normalizeMessages(messages);
    localStorage.setItem(cacheKey, JSON.stringify(normalized));
  } catch (e) {
    // Ignore cache errors (quota exceeded, etc.)
  }
};

const messagesInFlight = new Map();
const messagesFetchedAt = new Map();
const MESSAGES_MIN_REFETCH_MS = 3000;

export const getMessages = async (conversationId, useCache = true) => {
  if (!conversationId) {
    return { data: null, error: { message: 'Conversation ID is required' } };
  }

  try {
    if (useCache) {
      const cached = getCachedMessages(conversationId);
      if (cached) {
        const fetchedAt = messagesFetchedAt.get(conversationId) || 0;
        if (Date.now() - fetchedAt >= MESSAGES_MIN_REFETCH_MS && !messagesInFlight.has(conversationId)) {
          getMessages(conversationId, false).catch(() => {});
        }
        return { data: cached, error: null };
      }
    }

    const existing = messagesInFlight.get(conversationId);
    if (existing) {
      return existing;
    }

    const cached = getCachedMessages(conversationId);
    const fetchedAt = messagesFetchedAt.get(conversationId) || 0;
    if (cached && Date.now() - fetchedAt < MESSAGES_MIN_REFETCH_MS) {
      return { data: cached, error: null };
    }

    const token = getStoredToken();
    if (!token) {
      return { data: null, error: { message: 'Not authenticated' } };
    }

    const pending = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/messages?conversation_id=${conversationId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (!response.ok) {
          return { data: null, error: { message: data.error || 'Failed to fetch messages' } };
        }

        const normalized = normalizeMessages(data.data || []);
        cacheMessages(conversationId, normalized);
        messagesFetchedAt.set(conversationId, Date.now());
        return { data: normalized, error: null };
      } catch (error) {
        return { data: null, error: { message: error.message || 'Network error' } };
      }
    })().finally(() => {
      messagesInFlight.delete(conversationId);
    });

    messagesInFlight.set(conversationId, pending);
    return pending;
  } catch (error) {
    return { data: null, error: { message: error.message || 'Network error' } };
  }
};

export const sendMessage = async (conversationId, receiverId, content) => {
  try {
    const token = getStoredToken();
    if (!token) {
      return { data: null, error: { message: 'Not authenticated' } };
    }

    const normalizedContent = typeof content === 'string' ? content.trim() : '';
    if (normalizedContent.startsWith('SYSTEM_MESSAGE:')) {
      return {
        data: null,
        error: { message: 'Reserved message format is not allowed.' },
      };
    }

    const response = await fetch(`${API_BASE_URL}/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        receiver_id: receiverId,
        content: normalizedContent,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { data: null, error: { message: data.error || 'Failed to send message' } };
    }

    // Update cache with new message
    if (data.data) {
      const cached = getCachedMessages(conversationId) || [];
      cacheMessages(conversationId, [...cached, data.data]);
    }

    return { data: data.data, error: null };
  } catch (error) {
    return { data: null, error: { message: error.message || 'Network error' } };
  }
};

// Get cached conversations from localStorage
export const getCachedConversations = () => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const cached = localStorage.getItem('conversations');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null;
};

// Cache conversations to localStorage
export const cacheConversations = (conversations) => {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem('conversations', JSON.stringify(conversations));
  } catch (e) {
    // Ignore cache errors
  }
};

// Deduplicate conversations by user pair + request + listing. Request-scoped
// boosting threads and per-ad inquiries must not collapse into the general DM.
const deduplicateConversations = (conversations) => {
  const seen = new Map();
  const deduplicated = [];

  conversations.forEach(conv => {
    const key = conversationDedupeKey(conv);

    if (!seen.has(key)) {
      seen.set(key, conv);
      deduplicated.push(conv);
    } else {
      // Keep the one with the most recent message
      const existing = seen.get(key);
      const existingTime = new Date(existing.last_message_at || existing.created_at || 0).getTime();
      const currentTime = new Date(conv.last_message_at || conv.created_at || 0).getTime();
      
      if (currentTime > existingTime) {
        // Replace with newer conversation
        const index = deduplicated.findIndex(c => c.id === existing.id);
        if (index >= 0) {
          deduplicated[index] = conv;
          seen.set(key, conv);
        }
      }
    }
  });

  return deduplicated;
};

let conversationsFetch = null;

export const getConversations = async (useCache = true) => {
  try {
    // Return cached conversations immediately if available
    if (useCache) {
      const cached = getCachedConversations();
      if (cached) {
        const deduplicated = deduplicateConversations(cached);
        getConversations(false).then(({ data }) => {
          if (data) {
            cacheConversations(deduplicateConversations(data));
          }
        }).catch(() => {});
        return { data: deduplicated, error: null };
      }
    }

    const token = getStoredToken();
    if (!token) {
      return { data: null, error: { message: 'Not authenticated' } };
    }

    if (conversationsFetch) {
      return conversationsFetch;
    }

    conversationsFetch = (async () => {
      const response = await fetch(`${API_BASE_URL}/conversations`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return { data: null, error: { message: data.error || 'Failed to fetch conversations' } };
      }

      const deduplicated = deduplicateConversations(data.data || []);
      if (deduplicated.length > 0) {
        cacheConversations(deduplicated);
      }
      return { data: deduplicated, error: null };
    })().finally(() => {
      conversationsFetch = null;
    });

    return conversationsFetch;
  } catch (error) {
    return { data: null, error: { message: error.message || 'Network error' } };
  }
};

/**
 * Subscribe to real-time messages for a conversation.
 *
 * NOTE: Keep this **simple**. Supabase realtime already handles reconnects internally.
 * Custom polling/retry loops tend to create duplicate subscriptions and noisy logs.
 *
 * @param {string} conversationId - Conversation ID
 * @param {function} callback - Called with the raw inserted row (payload.new)
 * @returns {function} Unsubscribe function
 */
export const subscribeToMessages = (conversationId, callback) => {
  if (!conversationId || typeof callback !== 'function') {
    return () => {};
  }

  return subscribeWithAuth(`messages:${conversationId}`, [
    {
      event: 'INSERT',
      table: 'messages',
      filter: `conversation_id=eq.${conversationId}`,
      handler: (payload) => {
        if (!payload?.new) return;
        try {
          const cached = getCachedMessages(conversationId) || [];
          if (!cached.some((m) => m.id === payload.new.id)) {
            cacheMessages(conversationId, [...cached, payload.new]);
          }
        } catch {
          // ignore cache errors
        }
        callback(payload.new);
      },
    },
  ]);
};

